import { type Opportunity, type PartnerOrganization } from "@entities";
import { Typography } from "@mui/material";
import { opportunityRepo } from "@repositories";
import { type ColumnProps } from "@shared/frontend/hyper-table/types";
import { ADMIN_TABLE_PARTNER_VIA_PARENT } from "@shared/helpers/opportunity-group-admin-copy";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { useAlert } from "../../contexts/AlertProvider";
import { PartnerOrganizationSelect } from "./PartnerOrganizationSelect";

export const OpportunityPartnerOrganizationCell: FC<{
  opportunity: Opportunity;
}> = ({ opportunity }) => {
  const { setAlertText, setSeverity } = useAlert();
  const isGroupChild = Boolean(opportunity.parentOpportunityId);
  const [value, setValue] = useState<PartnerOrganization | null>(
    opportunity.partnerOrganization ?? null,
  );
  const opportunityRef = useRef(opportunity);
  const valueRef = useRef(value);
  opportunityRef.current = opportunity;
  valueRef.current = value;

  useEffect(() => {
    setValue(opportunity.partnerOrganization ?? null);
  }, [opportunity.partnerOrganization, opportunity.partnerOrganizationId]);

  const handleChange = useCallback(
    async (org: PartnerOrganization | null) => {
      const row = opportunityRef.current;
      const previous = valueRef.current;
      setValue(org);
      try {
        await opportunityRepo.update(row.id, {
          partnerOrganizationId: org?.id ?? null,
        });
        if (row.isOpportunityGroupParent) {
          await opportunityRepo.updateMany({
            where: {
              parentOpportunityId: row.id,
              isDeleted: false,
            },
            set: { partnerOrganizationId: org?.id ?? null },
          });
        }
        row.partnerOrganizationId = org?.id ?? null;
        row.partnerOrganization = org ?? undefined;
        setAlertText(org ? `שויך ל-${org.name}` : "שיוך הארגון הוסר");
        setSeverity("success");
      } catch {
        setValue(previous);
        setAlertText("לא הצלחנו לעדכן את שיוך הארגון השותף");
        setSeverity("error");
      }
    },
    [setAlertText, setSeverity],
  );

  if (isGroupChild) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
        {ADMIN_TABLE_PARTNER_VIA_PARENT}
      </Typography>
    );
  }

  return (
    <PartnerOrganizationSelect
      value={value}
      onChange={handleChange}
      label=""
      placeholder="בחר ארגון"
    />
  );
};

export const partnerOrganizationTableColumn: ColumnProps<Opportunity> = {
  field: "partnerOrganizationId",
  caption: "ארגון שותף",
  minSize: 220,
  enableSorting: false,
  preventRowClick: true,
  cell: ({ row }) => (
    <OpportunityPartnerOrganizationCell opportunity={row.original} />
  ),
};
